import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  CallToActionLink,
  Spacer
} from '@directsoftware/ui-kit-web-admin';
import ReactI18n from 'react-i18n';
import { selectReceiptState } from '../../../redux/slices/receipt';
import { formatCurrency } from '../../../../shared/helpers';

// Reservation-payment call to action. It belongs with the balance due, not with
// the security deposit: it opens the reservation payment page, which charges
// `payment_due_today`.
//
// `paymentState` is the canonical payment requirement. When it is supplied the
// call to action appears only for a payment the guest is actually being asked
// for today, so a balance Direct will charge itself, one due later, or one the
// channel collects never produces a prompt here. While the projection is still
// loading there is no prompt at all, so the legacy button cannot flash before
// the canonical state withdraws it.
const BalanceDueCta = ({ paymentState, isResolving, isFullWidth }) => {
  const receiptState = useSelector(selectReceiptState);
  const brand = useSelector(state => state.brand);
  const translate = ReactI18n.getIntlMessage;

  const { booking } = receiptState;
  const amountDue = Number(receiptState.payment_due_today || 0);

  if (!booking || !booking.confirmed || booking.cancelled || amountDue <= 0) {
    return null;
  }

  if (isResolving) return null;
  if (paymentState && !paymentState.guestActionRequired) return null;

  // The payment page only collects money while the booking still has a
  // remaining balance, so hide the call to action once it is fully paid.
  const remaining =
    booking.price_remaining !== undefined && booking.price_remaining !== null
      ? Number(booking.price_remaining)
      : Number(booking.price_total || 0) - Number(booking.price_paid || 0);

  if (remaining <= 0) return null;

  return (
    <Box>
      <Spacer size="s" />
      <CallToActionLink
        href={`/my-bookings/payment/${booking.booking_code}`}
        isFullWidth={isFullWidth}
        customButtonColor={
          brand.brand_info.colors && brand.brand_info.colors.color_primary
        }
        customTextColor={
          brand.brand_info.colors && brand.brand_info.colors.color_primary_text
        }
      >
        {translate(`cx.receipt.redesign.pay_amount_due`, {
          amount: formatCurrency(amountDue, brand.currency)
        })}
      </CallToActionLink>
    </Box>
  );
};

BalanceDueCta.propTypes = {
  paymentState: PropTypes.shape({
    status: PropTypes.string,
    guestActionRequired: PropTypes.bool
  }),
  isResolving: PropTypes.bool,
  isFullWidth: PropTypes.bool
};

BalanceDueCta.defaultProps = {
  paymentState: null,
  isResolving: false,
  isFullWidth: false
};

export default BalanceDueCta;
